import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { Link } from 'react-router-dom'

import { encodeQueryString } from '../../../../app/queryString'
import { Button, useSearchComboboxState } from '../../../components'
import { Search } from '../../../components/search'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { apiRequest } from '../../../services/api'
import { DEFAULT_QUERY_CACHE_TIME_IN_MS, InsiderView, ManagerSearchItemData } from '../constants'
import { useInsiderTradingRouter } from '../hooks/useInsiderTradingRouter'

interface Props {
  searchIn: 'managers' | 'funds'
}

export function ManagersAndFundsSearch({ searchIn }: Props) {
  const insiderTradingRouter = useInsiderTradingRouter<InsiderView.Managers | InsiderView.Funds>()
  const searchComboboxState = useSearchComboboxState({
    defaultValue: (insiderTradingRouter.viewParams.search as string) || '',
  })
  const isManagers = searchIn === 'managers'
  const isOpen = searchComboboxState.useState('open')
  const search = searchComboboxState.useState('value')
  const selectedValue = searchComboboxState.useState('selectedValue')
  const isSearchInputSelected = search === insiderTradingRouter.viewParams.search
  const trimmedSearch = search.trim()
  const debouncedSearch = useDebouncedValue({ value: trimmedSearch })
  const query = useQuery({
    queryKey: [searchIn, debouncedSearch],
    queryFn: async () => {
      const searchParams = encodeQueryString({ query: debouncedSearch })
      if (isManagers) {
        return apiRequest<ManagerSearchItemData[]>(`/api/managers/search?${searchParams}`)
      }

      return apiRequest<ManagerSearchItemData[]>(`/api/funds/search?${searchParams}`)
    },
    keepPreviousData: true,
    staleTime: DEFAULT_QUERY_CACHE_TIME_IN_MS,
    cacheTime: DEFAULT_QUERY_CACHE_TIME_IN_MS,
    enabled: isOpen && !!debouncedSearch && debouncedSearch.length > 0,
    useErrorBoundary: false,
  })

  const { hide } = searchComboboxState
  React.useEffect(() => {
    if (isOpen && trimmedSearch.length === 0) {
      hide()
    }
  }, [trimmedSearch, hide, isOpen])

  React.useEffect(() => {
    if (selectedValue) {
      const selectedItem = query.data?.find(({ seriesName, name }) => (seriesName || name) === selectedValue)
      if (!selectedItem) {
        window.location.href = insiderTradingRouter.getInsiderTradingLink({
          view: isManagers ? InsiderView.Managers : InsiderView.Funds,
          params: { search: selectedValue },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue])

  return (
    <Search
      autoFocus={true}
      state={searchComboboxState}
      isLoading={query.isLoading}
      getInputValue={(activeValue, value) =>
        query.data?.find(({ investorId }) => investorId === activeValue)?.name ?? value
      }
      inputProps={{
        className: 'w-full md:w-85 xl:w-110',
        placeholder: isManagers ? 'Search In Managers' : 'Search In Funds',
        isSelected: isSearchInputSelected,
        rightContent: isSearchInputSelected ? (
          <Button
            as={Link}
            to={insiderTradingRouter.getInsiderTradingLink({
              view: isManagers ? InsiderView.Managers : InsiderView.Funds,
            })}
            appearance="square"
            theme="link"
            rightContent="close"
            reloadDocument
          />
        ) : undefined,
      }}
      items={
        query.data?.map(({ investorId, seriesName, name }) => ({
          value: seriesName || name,
          label: seriesName || name,
          subtitle: seriesName ? name : undefined,
          href: insiderTradingRouter.getInsiderTradingLink({
            view: isManagers ? InsiderView.ManagerDetails : InsiderView.FundDetails,
            params: { investorId },
          }),
          reloadDocument: true,
        })) ?? []
      }
      footer={
        <Button
          as={Link}
          className="w-full shrink-0 self-center"
          contentClass="text-center"
          to={insiderTradingRouter.getInsiderTradingLink({
            view: isManagers ? InsiderView.Managers : InsiderView.Funds,
            params: { search },
          })}
          reloadDocument
        >
          Show All Results
        </Button>
      }
      isError={query.isError}
      onErrorRetry={query.refetch}
      isMatchInputAndBoxWidth
    />
  )
}
